import React, { useState, useMemo, useCallback } from 'react';
import { 
  Search, Filter, Calendar, DollarSign, ExternalLink, Check, 
  Clock, BookOpen, Users, Building, ChevronDown, ChevronUp, Heart, 
  AlertCircle, CheckCircle, XCircle, FileText, Globe
} from 'lucide-react';
import './IrelandScholarshipFinder.css';

// Scholarship Database
const SCHOLARSHIPS = [
  // Government Scholarships
  {
    id: 'gov-ireland',
    name: 'Government of Ireland International Education Scholarship',
    provider: 'Government of Ireland',
    providerType: 'government',
    amount: 10000,
    amountType: 'fixed',
    currency: 'EUR',
    description: 'Prestigious scholarship for high-achieving international students from non-EU/EEA countries.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Undergraduate', 'Postgraduate', 'PhD'],
      fields: ['All'],
      universities: ['All'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-03-31',
    applicationPeriod: 'January - March',
    awardsPerYear: 60,
    duration: '1 year (renewable)',
    coverage: ['Tuition fee reduction', 'Stipend for living expenses'],
    applicationLink: 'https://www.educationinireland.com/en/how-to-apply/scholarships/',
    applicationProcess: ['Apply to Irish institution first', 'Institution nominates candidates', 'Submit scholarship application'],
    documents: ['Academic transcripts', 'Personal statement', 'Two reference letters', 'CV/Resume', 'English proficiency proof'],
    competitionLevel: 'high',
  },
  {
    id: 'icsp',
    name: 'International Climate Stewardship Programme (ICSP)',
    provider: 'Government of Ireland',
    providerType: 'government',
    amount: 15000,
    amountType: 'fixed',
    currency: 'EUR',
    description: 'Scholarship for students from developing countries pursuing climate-related studies.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction', 'Second Class Upper'],
      courseLevels: ['Postgraduate'],
      fields: ['Environmental Science', 'Climate Change', 'Sustainability', 'Renewable Energy'],
      universities: ['All'],
      financialNeed: true,
      indianSpecific: true
    },
    deadline: '2025-02-28',
    applicationPeriod: 'November - February',
    awardsPerYear: 20,
    duration: 'Full program duration',
    coverage: ['Full tuition', 'Monthly stipend', 'Travel allowance'],
    applicationLink: 'https://www.irishaid.ie/',
    applicationProcess: ['Online application', 'Submit research proposal', 'Interview'],
    documents: ['Academic transcripts', 'Research proposal', 'Financial need proof', 'Climate commitment statement'],
    competitionLevel: 'high',
  },
  {
    id: 'enterprise-ireland',
    name: 'Enterprise Ireland Innovation Partnership',
    provider: 'Enterprise Ireland',
    providerType: 'government',
    amount: 20000,
    amountType: 'fixed',
    currency: 'EUR',
    description: 'Funding for postgraduate students working on industry-partnered research projects.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Postgraduate', 'PhD'],
      fields: ['Computer Science', 'Engineering', 'Data Science', 'Biotechnology'],
      universities: ['TCD', 'UCD', 'DCU', 'UL', 'NUIG', 'UCC', 'TU Dublin'],
      financialNeed: false,
      indianSpecific: false
    },
    deadline: '2025-04-15',
    applicationPeriod: 'Rolling',
    awardsPerYear: 50,
    duration: '2-4 years',
    coverage: ['Full tuition', 'Research funding', 'Industry placement'],
    applicationLink: 'https://www.enterprise-ireland.com/',
    applicationProcess: ['Find industry partner', 'Develop proposal', 'Submit joint application'],
    documents: ['Research proposal', 'Industry partnership agreement', 'Academic transcripts', 'CV'],
    competitionLevel: 'medium',
  },

  // University-Specific Scholarships
  {
    id: 'tcd-global',
    name: 'Trinity College Dublin Global Excellence Scholarship',
    provider: 'Trinity College Dublin',
    providerType: 'university',
    amount: 5000,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Merit-based scholarship for outstanding international students at Trinity College Dublin.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['TCD'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-05-01',
    applicationPeriod: 'March - May',
    awardsPerYear: 30,
    duration: '1 year (renewable)',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.tcd.ie/study/international/scholarships/',
    applicationProcess: ['Apply to TCD', 'Automatic consideration', 'Receive offer with scholarship'],
    documents: ['Academic transcripts', 'Personal statement'],
    competitionLevel: 'medium',
  },
  {
    id: 'ucd-global',
    name: 'UCD Global Excellence Scholarship',
    provider: 'University College Dublin',
    providerType: 'university',
    amount: 6000,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Scholarship for high-achieving international students at UCD.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['UCD'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-04-30',
    applicationPeriod: 'February - April',
    awardsPerYear: 40,
    duration: '1 year (renewable)',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.ucd.ie/global/scholarships/',
    applicationProcess: ['Apply to UCD', 'Submit scholarship application', 'Interview for shortlisted'],
    documents: ['Academic transcripts', 'Personal statement', 'CV', 'Reference letter'],
    competitionLevel: 'medium',
  },
  {
    id: 'dcu-international',
    name: 'DCU International Student Scholarship',
    provider: 'Dublin City University',
    providerType: 'university',
    amount: 4000,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Scholarship for international students demonstrating academic excellence at DCU.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction', 'Second Class Upper'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['DCU'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-05-15',
    applicationPeriod: 'March - May',
    awardsPerYear: 25,
    duration: '1 year',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.dcu.ie/international/scholarships',
    applicationProcess: ['Apply to DCU', 'Complete scholarship form', 'Automatic consideration'],
    documents: ['Academic transcripts', 'Personal statement'],
    competitionLevel: 'low',
  },
  {
    id: 'ul-global',
    name: 'University of Limerick Global Scholarship',
    provider: 'University of Limerick',
    providerType: 'university',
    amount: 4500,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Merit-based scholarship for international students at University of Limerick.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction', 'Second Class Upper'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['UL'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-06-01',
    applicationPeriod: 'April - June',
    awardsPerYear: 35,
    duration: '1 year (renewable)',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.ul.ie/international/scholarships',
    applicationProcess: ['Apply to UL', 'Submit scholarship application', 'Receive decision'],
    documents: ['Academic transcripts', 'Personal statement', 'Reference letter'],
    competitionLevel: 'low',
  },
  {
    id: 'nuig-international',
    name: 'NUIG International Student Scholarship',
    provider: 'National University of Ireland Galway',
    providerType: 'university',
    amount: 4000,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Scholarship for academically excellent international students at NUIG.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['NUIG'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-05-30',
    applicationPeriod: 'March - May',
    awardsPerYear: 30,
    duration: '1 year',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.universityofgalway.ie/international/scholarships/',
    applicationProcess: ['Apply to NUIG', 'Automatic consideration', 'Scholarship offer with admission'],
    documents: ['Academic transcripts', 'Personal statement'],
    competitionLevel: 'medium',
  },
  {
    id: 'ucc-international',
    name: 'UCC International Scholarship',
    provider: 'University College Cork',
    providerType: 'university',
    amount: 5000,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Merit-based scholarship for international students at UCC.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['UCC'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-05-01',
    applicationPeriod: 'February - May',
    awardsPerYear: 28,
    duration: '1 year (renewable)',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.ucc.ie/en/international/scholarships/',
    applicationProcess: ['Apply to UCC', 'Submit scholarship application', 'Interview'],
    documents: ['Academic transcripts', 'Personal statement', 'CV', 'Two references'],
    competitionLevel: 'medium',
  },
  {
    id: 'tudublin-international',
    name: 'TU Dublin International Scholarship',
    provider: 'Technological University Dublin',
    providerType: 'university',
    amount: 3000,
    amountType: 'tuition-reduction',
    currency: 'EUR',
    description: 'Scholarship for international students at TU Dublin.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction', 'Second Class Upper', 'Second Class Lower'],
      courseLevels: ['Undergraduate', 'Postgraduate'],
      fields: ['All'],
      universities: ['TU Dublin'],
      financialNeed: false,
      indianSpecific: true
    },
    deadline: '2025-06-15',
    applicationPeriod: 'April - June',
    awardsPerYear: 50,
    duration: '1 year',
    coverage: ['Tuition fee reduction'],
    applicationLink: 'https://www.tudublin.ie/international/scholarships/',
    applicationProcess: ['Apply to TU Dublin', 'Automatic consideration', 'Scholarship offer'],
    documents: ['Academic transcripts'],
    competitionLevel: 'low',
  },

  // Indian Government Scholarships
  {
    id: 'iccr',
    name: 'ICCR Scholarship for Ireland',
    provider: 'Indian Council for Cultural Relations',
    providerType: 'government',
    amount: 8000,
    amountType: 'annual',
    currency: 'EUR',
    description: 'Indian government scholarship for students pursuing higher education in Ireland.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction', 'Second Class Upper'],
      courseLevels: ['Undergraduate', 'Postgraduate', 'PhD'],
      fields: ['All'],
      universities: ['All'],
      financialNeed: true,
      indianSpecific: true
    },
    deadline: '2025-01-31',
    applicationPeriod: 'November - January',
    awardsPerYear: 15,
    duration: 'Full program duration',
    coverage: ['Tuition fees', 'Monthly allowance', 'Airfare'],
    applicationLink: 'https://iccr.gov.in/',
    applicationProcess: ['Apply through ICCR portal', 'Submit documents', 'Interview at Indian mission'],
    documents: ['Academic transcripts', 'Income certificate', 'Passport copy', 'Admission letter', 'Medical fitness certificate'],
    competitionLevel: 'high',
  },
  {
    id: 'national-overseas',
    name: 'National Overseas Scholarship',
    provider: 'Ministry of Social Justice & Empowerment, India',
    providerType: 'government',
    amount: 12000,
    amountType: 'annual',
    currency: 'EUR',
    description: 'Scholarship for SC/ST/OBC/Minority students pursuing higher education abroad.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction', 'Second Class Upper'],
      courseLevels: ['Postgraduate', 'PhD'],
      fields: ['All'],
      universities: ['All'],
      financialNeed: true,
      indianSpecific: true
    },
    deadline: '2025-02-15',
    applicationPeriod: 'December - February',
    awardsPerYear: 100,
    duration: 'Full program duration',
    coverage: ['Full tuition', 'Monthly stipend', 'Airfare', 'Books allowance'],
    applicationLink: 'https://nosmsje.gov.in/',
    applicationProcess: ['Online application', 'Document verification', 'Interview', 'Selection'],
    documents: ['Caste certificate', 'Income certificate', 'Academic transcripts', 'Admission letter', 'Passport'],
    competitionLevel: 'high',
  },

  // Private/Organization Scholarships
  {
    id: 'commonwealth',
    name: 'Commonwealth Scholarship (Ireland)',
    provider: 'Commonwealth Scholarship Commission',
    providerType: 'organization',
    amount: 18000,
    amountType: 'annual',
    currency: 'EUR',
    description: 'Prestigious scholarship for Commonwealth citizens pursuing development-focused studies.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Postgraduate', 'PhD'],
      fields: ['Development Studies', 'Public Health', 'Engineering', 'Education'],
      universities: ['TCD', 'UCD', 'NUIG', 'UCC'],
      financialNeed: true,
      indianSpecific: false
    },
    deadline: '2025-10-15',
    applicationPeriod: 'August - October',
    awardsPerYear: 10,
    duration: 'Full program duration',
    coverage: ['Full tuition', 'Stipend', 'Airfare', 'Research grant'],
    applicationLink: 'https://cscuk.fcdo.gov.uk/',
    applicationProcess: ['Apply through nominating agency', 'Submit application', 'Interview'],
    documents: ['Academic transcripts', 'Development impact statement', 'References', 'Admission letter'],
    competitionLevel: 'very-high',
  },
  {
    id: 'rotary',
    name: 'Rotary Foundation Global Grant',
    provider: 'Rotary International',
    providerType: 'organization',
    amount: 30000,
    amountType: 'fixed',
    currency: 'EUR',
    description: 'Funding for graduate-level coursework or research in one of Rotary\'s areas of focus.',
    eligibility: {
      academicMerit: ['First Class', 'Distinction'],
      courseLevels: ['Postgraduate', 'PhD'],
      fields: ['Healthcare', 'Education', 'Environmental Science', 'Peace Studies'],
      universities: ['All'],
      financialNeed: false,
      indianSpecific: false
    },
    deadline: '2025-05-31',
    applicationPeriod: 'Rolling',
    awardsPerYear: 400,
    duration: '1-4 years',
    coverage: ['Tuition', 'Living expenses', 'Travel', 'Project funding'],
    applicationLink: 'https://www.rotary.org/en/grants',
    applicationProcess: ['Find Rotary club sponsor', 'Develop project plan', 'Submit application'],
    documents: ['Project proposal', 'Budget', 'Academic transcripts', 'References', 'Host sponsor letter'],
    competitionLevel: 'medium',
  }
];

const ACADEMIC_MERIT_OPTIONS = [
  { id: 'first-class', name: 'First Class (60%+)', value: 'First Class' },
  { id: 'distinction', name: 'Distinction (70%+)', value: 'Distinction' },
  { id: 'second-upper', name: 'Second Class Upper (55-60%)', value: 'Second Class Upper' },
  { id: 'second-lower', name: 'Second Class Lower (50-55%)', value: 'Second Class Lower' }
];

const COURSE_LEVELS = [
  { id: 'undergraduate', name: 'Undergraduate' },
  { id: 'postgraduate', name: 'Postgraduate' },
  { id: 'phd', name: 'PhD' }
];

const FIELD_OPTIONS = [
  'All', 'Computer Science', 'Business', 'Engineering', 'Data Science', 
  'Healthcare', 'Arts', 'Law', 'Environmental Science', 'Climate Change',
  'Sustainability', 'Renewable Energy', 'Biotechnology', 'Education',
  'Development Studies', 'Public Health', 'Peace Studies'
];

const UNIVERSITY_OPTIONS = [
  { id: 'All', name: 'Any University' },
  { id: 'TCD', name: 'Trinity College Dublin' },
  { id: 'UCD', name: 'University College Dublin' },
  { id: 'DCU', name: 'Dublin City University' },
  { id: 'UL', name: 'University of Limerick' },
  { id: 'NUIG', name: 'National University of Ireland Galway' },
  { id: 'UCC', name: 'University College Cork' },
  { id: 'TU Dublin', name: 'Technological University Dublin' }
];

export const isDeadlineCurrent = (deadline, now = new Date()) =>
  new Date(`${deadline}T23:59:59`) >= now;

const IrelandScholarshipFinder = () => {
  const [filters, setFilters] = useState({
    academicMerit: '',
    courseLevel: '',
    field: '',
    university: '',
    financialNeed: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedScholarship, setExpandedScholarship] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [sortBy, setSortBy] = useState('deadline');
  const [showFilters, setShowFilters] = useState(true);

  // Calculate days until deadline
  const getDaysUntilDeadline = (deadline) => {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check eligibility
  const checkEligibility = useCallback((scholarship) => {
    const results = [];
    
    // Academic merit check
    if (filters.academicMerit) {
      const meritValue = ACADEMIC_MERIT_OPTIONS.find(m => m.id === filters.academicMerit)?.value;
      if (scholarship.eligibility.academicMerit.includes(meritValue)) {
        results.push({ field: 'Academic Merit', met: true, message: `${meritValue} meets requirements` });
      } else {
        results.push({ field: 'Academic Merit', met: false, message: `Requires: ${scholarship.eligibility.academicMerit.join(' or ')}` });
      }
    }

    // Course level check
    if (filters.courseLevel) {
      const levelName = COURSE_LEVELS.find(l => l.id === filters.courseLevel)?.name;
      if (scholarship.eligibility.courseLevels.includes(levelName)) {
        results.push({ field: 'Course Level', met: true, message: `${levelName} eligible` });
      } else {
        results.push({ field: 'Course Level', met: false, message: `Not available for ${levelName}` });
      }
    }

    // Field check
    if (filters.field) {
      if (scholarship.eligibility.fields.includes('All') || scholarship.eligibility.fields.includes(filters.field)) {
        results.push({ field: 'Field of Study', met: true, message: `${filters.field} eligible` });
      } else {
        results.push({ field: 'Field of Study', met: false, message: `Fields: ${scholarship.eligibility.fields.join(', ')}` });
      }
    }

    // University check
    if (filters.university) {
      if (scholarship.eligibility.universities.includes('All') || scholarship.eligibility.universities.includes(filters.university)) {
        results.push({ field: 'University', met: true, message: 'University eligible' });
      } else {
        results.push({ field: 'University', met: false, message: `Universities: ${scholarship.eligibility.universities.join(', ')}` });
      }
    }

    // Financial need check
    if (filters.financialNeed) {
      const needsFinancialAid = filters.financialNeed === 'yes';
      if (!scholarship.eligibility.financialNeed || needsFinancialAid) {
        results.push({ field: 'Financial Need', met: true, message: 'Requirement met' });
      } else {
        results.push({ field: 'Financial Need', met: false, message: 'Financial need required' });
      }
    }

    return results;
  }, [filters]);

  // Filter and sort scholarships
  const filteredScholarships = useMemo(() => {
    let results = SCHOLARSHIPS.filter(scholarship => {
      // Never present an expired deadline as an actionable opportunity.
      if (!isDeadlineCurrent(scholarship.deadline)) return false;

      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!scholarship.name.toLowerCase().includes(query) &&
            !scholarship.provider.toLowerCase().includes(query) &&
            !scholarship.description.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Academic merit filter
      if (filters.academicMerit) {
        const meritValue = ACADEMIC_MERIT_OPTIONS.find(m => m.id === filters.academicMerit)?.value;
        if (!scholarship.eligibility.academicMerit.includes(meritValue)) {
          return false;
        }
      }

      // Course level filter
      if (filters.courseLevel) {
        const levelName = COURSE_LEVELS.find(l => l.id === filters.courseLevel)?.name;
        if (!scholarship.eligibility.courseLevels.includes(levelName)) {
          return false;
        }
      }

      // Field filter
      if (filters.field && filters.field !== 'All') {
        if (!scholarship.eligibility.fields.includes('All') && 
            !scholarship.eligibility.fields.includes(filters.field)) {
          return false;
        }
      }

      // University filter
      if (filters.university && filters.university !== 'All') {
        if (!scholarship.eligibility.universities.includes('All') && 
            !scholarship.eligibility.universities.includes(filters.university)) {
          return false;
        }
      }

      // Financial need filter
      if (filters.financialNeed === 'yes' && !scholarship.eligibility.financialNeed) {
        // If user needs financial aid, show scholarships that consider it
      }

      return true;
    });

    // Sort results
    results.sort((a, b) => {
      switch (sortBy) {
        case 'deadline':
          return new Date(a.deadline) - new Date(b.deadline);
        case 'amount':
          return b.amount - a.amount;
        default:
          return 0;
      }
    });

    return results;
  }, [filters, searchQuery, sortBy]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      academicMerit: '',
      courseLevel: '',
      field: '',
      university: '',
      financialNeed: ''
    });
    setSearchQuery('');
  };

  const toggleFavorite = (id) => {
    if (favorites.includes(id)) {
      setFavorites(favorites.filter(f => f !== id));
    } else {
      setFavorites([...favorites, id]);
    }
  };

  const getDeadlineStatus = (deadline) => {
    const days = getDaysUntilDeadline(deadline);
    if (days < 0) return { status: 'passed', label: 'Deadline Passed', color: '#ef4444' };
    if (days <= 30) return { status: 'urgent', label: `${days} days left`, color: '#ef4444' };
    if (days <= 60) return { status: 'soon', label: `${days} days left`, color: '#f59e0b' };
    return { status: 'normal', label: `${days} days left`, color: '#10b981' };
  };

  const getCompetitionBadge = (level) => {
    switch (level) {
      case 'very-high':
        return { label: 'Very Competitive', color: '#ef4444' };
      case 'high':
        return { label: 'Competitive', color: '#f59e0b' };
      case 'medium':
        return { label: 'Moderate', color: '#3b82f6' };
      case 'low':
         return { label: 'Lower competition', color: '#10b981' };
      default:
        return { label: 'Standard', color: '#6b7280' };
    }
  };

  const renderFilterSidebar = () => (
    <div className={`filter-sidebar ${showFilters ? 'show' : ''}`}>
      <div className="filter-header">
        <h3><Filter size={18} /> Filters</h3>
        <button className="clear-filters" onClick={clearFilters}>
          Clear All
        </button>
      </div>

      <div className="filter-group">
        <label>Academic Merit</label>
        <select 
          value={filters.academicMerit}
          onChange={(e) => handleFilterChange('academicMerit', e.target.value)}
        >
          <option value="">Any</option>
          {ACADEMIC_MERIT_OPTIONS.map(option => (
            <option key={option.id} value={option.id}>{option.name}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <label>Course Level</label>
        <select 
          value={filters.courseLevel}
          onChange={(e) => handleFilterChange('courseLevel', e.target.value)}
        >
          <option value="">Any</option>
          {COURSE_LEVELS.map(level => (
            <option key={level.id} value={level.id}>{level.name}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <label>Field of Study</label>
        <select 
          value={filters.field}
          onChange={(e) => handleFilterChange('field', e.target.value)}
        >
          <option value="">Any</option>
          {FIELD_OPTIONS.map(field => (
            <option key={field} value={field}>{field}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <label>Target University</label>
        <select 
          value={filters.university}
          onChange={(e) => handleFilterChange('university', e.target.value)}
        >
          {UNIVERSITY_OPTIONS.map(uni => (
            <option key={uni.id} value={uni.id}>{uni.name}</option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <label>Financial Need</label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name="financialNeed"
              checked={filters.financialNeed === ''}
              onChange={() => handleFilterChange('financialNeed', '')}
            />
            Any
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="financialNeed"
              checked={filters.financialNeed === 'yes'}
              onChange={() => handleFilterChange('financialNeed', 'yes')}
            />
            Yes
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="financialNeed"
              checked={filters.financialNeed === 'no'}
              onChange={() => handleFilterChange('financialNeed', 'no')}
            />
            No
          </label>
        </div>
      </div>

      <div className="filter-stats">
        <span className="result-count">{filteredScholarships.length} scholarships found</span>
      </div>
    </div>
  );

  const renderScholarshipCard = (scholarship) => {
    const deadlineStatus = getDeadlineStatus(scholarship.deadline);
    const competition = getCompetitionBadge(scholarship.competitionLevel);
    const eligibilityResults = checkEligibility(scholarship);
    const isExpanded = expandedScholarship === scholarship.id;
    const isFavorite = favorites.includes(scholarship.id);

    return (
      <div key={scholarship.id} className={`scholarship-card ${deadlineStatus.status}`}>
        <div className="card-top">
          <div className="provider-badge" style={{ 
            backgroundColor: scholarship.providerType === 'government' ? 'rgba(22, 155, 98, 0.2)' :
                            scholarship.providerType === 'university' ? 'rgba(59, 130, 246, 0.2)' :
                            'rgba(255, 136, 51, 0.2)',
            color: scholarship.providerType === 'government' ? '#169B62' :
                   scholarship.providerType === 'university' ? '#3b82f6' : '#FF8833'
          }}>
            {scholarship.providerType === 'government' && <Building size={14} />}
            {scholarship.providerType === 'university' && <BookOpen size={14} />}
            {scholarship.providerType === 'organization' && <Globe size={14} />}
            {scholarship.provider}
          </div>
          <div className="deadline-badge" style={{ backgroundColor: `${deadlineStatus.color}20`, color: deadlineStatus.color }}>
            <Clock size={14} />
            {deadlineStatus.label}
          </div>
        </div>

        <h3 className="scholarship-name">{scholarship.name}</h3>
        <p className="scholarship-description">{scholarship.description}</p>

        <div className="amount-section">
          <div className="amount-badge">
            <DollarSign size={18} />
            <span className="amount">€{scholarship.amount.toLocaleString()}</span>
            <span className="amount-type">
              {scholarship.amountType === 'fixed' ? 'one-time' : 
               scholarship.amountType === 'annual' ? 'per year' : 'tuition reduction'}
            </span>
          </div>
          <div className="competition-badge" style={{ backgroundColor: `${competition.color}20`, color: competition.color }}>
            {competition.label}
          </div>
        </div>

        <div className="quick-info">
          <div className="info-item">
            <Users size={14} />
            <span>{scholarship.awardsPerYear} awards/year</span>
          </div>
          <div className="info-item">
            <Calendar size={14} />
            <span>Deadline: {new Date(scholarship.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>

        {/* Eligibility Checklist */}
        {(filters.academicMerit || filters.courseLevel || filters.field || filters.university) && (
          <div className="eligibility-checklist">
            {eligibilityResults.map((result, idx) => (
              <div key={idx} className={`checklist-item ${result.met ? 'met' : 'unmet'}`}>
                {result.met ? <CheckCircle size={14} /> : <XCircle size={14} />}
                <span>{result.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Expandable Details */}
        {isExpanded && (
          <div className="expanded-details">
            <div className="detail-section">
              <h4>Coverage</h4>
              <ul>
                {scholarship.coverage.map((item, idx) => (
                  <li key={idx}><Check size={14} /> {item}</li>
                ))}
              </ul>
            </div>

            <div className="detail-section">
              <h4>Application Process</h4>
              <ol>
                {scholarship.applicationProcess.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="detail-section">
              <h4>Required Documents</h4>
              <div className="document-list">
                {scholarship.documents.map((doc, idx) => (
                  <span key={idx} className="document-tag">
                    <FileText size={12} /> {doc}
                  </span>
                ))}
              </div>
            </div>

            <div className="detail-section">
              <h4>Duration</h4>
              <p>{scholarship.duration}</p>
            </div>

            <a 
              href={scholarship.applicationLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="apply-button"
            >
              Apply Now <ExternalLink size={14} />
            </a>
          </div>
        )}

        <div className="card-actions">
          <button 
            className="expand-btn"
            onClick={() => setExpandedScholarship(isExpanded ? null : scholarship.id)}
          >
            {isExpanded ? <>Show Less <ChevronUp size={14} /></> : <>View Details <ChevronDown size={14} /></>}
          </button>
          <button 
            className={`favorite-btn ${isFavorite ? 'active' : ''}`}
            onClick={() => toggleFavorite(scholarship.id)}
          >
            <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="scholarship-finder">
      <div className="finder-header">
        <h2>🎓 Ireland Scholarship Finder</h2>
        <p className="finder-subtitle">Discover scholarships available for Indian students to study in Ireland</p>
        <p className="finder-disclaimer">Deadlines and awards change by intake. Verify every opportunity on the linked provider page before applying.</p>
      </div>

      {/* Search and Sort Bar */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search scholarships by name, provider, or keyword..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="sort-select">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="deadline">Deadline</option>
            <option value="amount">Amount</option>
          </select>
        </div>
        <button 
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={18} />
          Filters
        </button>
      </div>

      {/* Main Content */}
      <div className="finder-content">
        {renderFilterSidebar()}
        
        <div className="scholarships-grid">
          {filteredScholarships.length > 0 ? (
            filteredScholarships.map(renderScholarshipCard)
          ) : (
            <div className="no-results">
              <AlertCircle size={48} />
              <h3>No scholarships found</h3>
              <p>No current matching opportunities are in the local catalog. Check the linked provider and university scholarship pages for the latest cycle.</p>
              <button onClick={clearFilters}>Clear Filters</button>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-item">
          <span className="stat-value">{SCHOLARSHIPS.length}</span>
          <span className="stat-label">Catalog records (includes expired)</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">€{Math.max(...SCHOLARSHIPS.map(s => s.amount)).toLocaleString()}</span>
          <span className="stat-label">Max Award</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{SCHOLARSHIPS.reduce((sum, s) => sum + s.awardsPerYear, 0)}</span>
          <span className="stat-label">Awards/Year</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{favorites.length}</span>
          <span className="stat-label">Saved</span>
        </div>
      </div>
    </div>
  );
};

export default IrelandScholarshipFinder;
